import { Badge, badgeVariants } from "components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { ComponentProps, useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { SearchIcon, X } from "lucide-react";
import { isDefined, isPrimitive } from "helper/utils";

import { ChevronDownIcon } from "@/assets";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

export type OptionData<T> = {
  value: T;
  label: string;
  description?: string;
};

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

export type DropdownMultiSelectProps<T = string> = {
  values: T[];
  onChangeValues: (values: T[], label?: string) => void;
  badgeVariant?: BadgeVariant;
  options: OptionData<T>[];
  onInputValueChange?: (inputValue: string) => void;
  classNamePopover?: string;
  hasMore?: boolean;
  loadMoreItems?: () => void;
} & Omit<ComponentProps<"input">, "onChange" | "onKeyDown" | "value" | "size"> &
  Pick<VariantProps<typeof buttonVariants>, "variant">;

export const DropdownMultiSelect = <T extends string>({
  values,
  onChangeValues,
  badgeVariant = "default",
  className,
  options,
  onInputValueChange,
  classNamePopover,
  variant = "outline",
  ...inputProps
}: DropdownMultiSelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [listOptions, setListOptions] = useState<OptionData<T>[]>([]);

  const onInputChange = (value: string) => {
    setInputValue(value);
    if (onInputValueChange) {
      onInputValueChange(value.trim());
    }
  };

  const optionData = useMemo(
    () =>
      options.map(
        (opt) =>
          (isPrimitive(opt)
            ? {
                value: opt,
                label: String(opt),
              }
            : opt) as OptionData<T>,
      ),
    [options],
  );

  const selectedItems = useMemo(
    () => (optionData ? values.map((v) => optionData.find((opt) => opt.value === v)).filter(isDefined) : []),
    [values, optionData],
  );

  useEffect(() => {
    const requestId = window.setTimeout(() => {
      const term = inputValue.toLowerCase();
      const newOptions = optionData.filter(
        (p) =>
          selectedItems.indexOf(p) === -1 &&
          (p.label.toLowerCase().includes(term) || (p.description?.toLowerCase().includes(term) ?? false)),
      );
      setListOptions(newOptions);
    }, 500);

    return () => window.clearTimeout(requestId);
  }, [inputValue, selectedItems, optionData]);

  // Remove a single value
  const removeSelectedItem = (value: string) => {
    const newValues = values.filter((val) => val !== value);
    onChangeValues(newValues);
  };

  // select an item
  const selectItem = (value: T) => {
    onChangeValues([...values, value]);
  };
  // Clear all selected values
  const clearValues = () => {
    onChangeValues([]);
  };

  return (
    <div>
      <div className="w-full max-w-sm">
        {values.length > 0 && (
          <div className="flex flex-wrap gap-1 overflow-x-auto max-w-full mb-1">
            {selectedItems.map((item, index) => {
              return (
                <Badge
                  key={`selectedItem-${item.value}-${index}`}
                  variant={badgeVariant}
                  className="max-w-full break-words whitespace-normal"
                >
                  <span className="flex flex-col items-start">
                    <span>{item.label}</span>
                    {item.description && (
                      <span className="text-[10px] leading-tight opacity-80">{item.description}</span>
                    )}
                  </span>
                  <button
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        removeSelectedItem(item.value);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={() => removeSelectedItem(item.value)}
                    type="button"
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-red-500" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button variant={variant} aria-expanded={open} className={cn("w-full justify-between", className)}>
              {inputProps.placeholder}
              <ChevronDownIcon
                className={`${open && "rotate-180 transform"} transition duration-150 ease-in-out w-5`}
                aria-hidden="true"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className={cn("w-full", classNamePopover)}>
            <Command>
              <div className="flex h-9 items-center gap-2 border-b px-3">
                <SearchIcon className="size-4 shrink-0 opacity-50" />
                <input
                  className="placeholder:text-muted-foreground flex h-10 w-full bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={inputProps.placeholder}
                  onChange={(e) => onInputChange(e.target.value)}
                  value={inputValue}
                />
              </div>
              <CommandEmpty>No record found.</CommandEmpty>
              <CommandGroup className="max-h-52 overflow-y-auto">
                {listOptions?.map((item, idx) => (
                  <CommandItem
                    key={`suggestion-${item.value}-${idx}`}
                    onSelect={() => selectItem(item.value)}
                    className="cursor-pointer"
                  >
                    <span className="flex flex-col">
                      <span>{item.label}</span>
                      {item.description && <span className="text-xs text-muted-foreground">{item.description}</span>}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
              {values.length > 0 && (
                <div className="border-t p-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      clearValues();
                      setOpen(false);
                    }}
                  >
                    Clear selections
                  </Button>
                </div>
              )}
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
